/**
 * سربرگِ کرالر — «خطِ لوله دارد چه می‌کند و اگر مشکلی هست، از کجا».
 *
 * ═══ ⚠ بازنویسیِ ۲۰۲۶-۰۸-۲۰ ═══
 *
 * این صفحه چند روز فقط `[object Object]` نشان می‌داد. خرابی نبود: مخزنِ
 * event_crawler خطِ لوله را عوض کرد و جدول‌هایی که تابعِ پشتِ این صفحه
 * رویشان بنا شده بود (`raw_items`، `review_queue`، `alerts`،
 * `source_health`) دیگر وجود ندارند. مایگریشنِ ۰۰۹۰ همه را روی اسکیمای
 * vNext از نو نوشت و این فایل آن را نشان می‌دهد.
 *
 * درسی که ارزشِ نوشتن دارد: صفحه **خطا** می‌داد ولی متنِ خطا را دور
 * می‌انداخت (`String(error)` روی شیءِ خطای سوپابیس یعنی `[object Object]`).
 * چند روز طول کشید تا معلوم شود مشکل چیست، در حالی که خودِ دیتابیس از روزِ
 * اول اسمِ جدولِ گم‌شده را می‌گفت. حالا پیامِ واقعی چاپ می‌شود.
 *
 * ═══ ⚠ هر عدد دو تاست، و انتخابگرِ پنجره رفت ═══
 *
 * تا مایگریشنِ ۰۱۰۸ بالای این صفحه کشوییِ «۷ / ۳۰ / ۹۰ روز» بود و نیمی
 * از اعداد اصلاً به آن بند نبودند (`win_*` بود و بقیه «الان»). حالا هر
 * عدد همیشه دو تاست — **۱۰ روزِ اخیر** و **آخرین اجرا** — و هرجا معنی
 * داشته باشد «از N روی هم» ریز زیرش. شکلش در `ui/Num.tsx` است و توضیحِ
 * کاملش در سرِ همان فایل.
 *
 * ⚠ روی جدولِ upsertشونده (کاندیدا، رویدادِ نهایی، حکمِ سیاست) این دو عدد
 * یعنی «دست‌خورده»، نه «ساخته‌شده»؛ و خوشه استثناست چون عضویتِ یک نسل
 * یکجا نوشته می‌شود. `total` برای همین کنارشان ماند.
 *
 * ترتیبِ بخش‌ها از «چه شد» به «چرا» می‌رود:
 *   ۱. فعالیت — اصلاً اجرا شده؟
 *   ۲. سنکیِ درخواست — از آدرس‌های در صف، چند تا به شبکه رسید و بقیه کجا ماند
 *   ۳. سنکیِ خطِ لوله — شکلِ کلیِ جریان و اینکه کجا ریخت
 *   ۴. مرحله‌به‌مرحله — همان زنجیره از دیدِ **مرحله‌ها**، با کارنامه‌ی هر کدام
 *   ۵. ریزش‌ها — همان‌ها به‌صورت عدد، با نمونه‌ی واقعی زیرشان
 *   ۶. فازها — کدام مرحله کند است، کدام می‌شکند، آخرین خطایش چه بود
 *   ۷. منابع — کدام منبع دیگر چیزی نمی‌دهد
 *
 * ⚠ **دو نمودار، و چسباندنشان به هم غلط است.** واحدِ اولی «یک آدرس» است و
 * واحدِ دومی «یک رویدادِ نامزد»؛ یک صفحه‌ی فهرست سی کاندیدا می‌دهد. همان
 * دلیلی که نمودارِ دوم از «سند» شروع نمی‌شود، این‌جا یک پله بالاتر تکرار
 * می‌شود. تاریخچه‌اش سرِ `crawlerFlow.ts` و مایگریشنِ ۰۱۱۵ است.
 *
 * ⚠ **و نمودارِ سوم هیچ‌کدامشان را تکرار نمی‌کند.** آن دو تا جریانِ یک
 * **واحد** را دنبال می‌کنند (یک آدرس، یک رویدادِ نامزد). سومی واحد را عوض
 * می‌کند و به **مرحله** نگاه می‌کند: هر مرحله چه کار می‌کند، چقدر کار به آن
 * رسیده، خودش چند بار شکسته و خطایش چه جنسی بوده. یعنی تنها جای صفحه که
 * سلامتِ فازها (جدولِ پایین) و جریانِ آیتم‌ها (سنکی‌های بالا) در یک ردیف
 * کنارِ هم‌اند. حسابش در `core/lib/stageReport.ts` است و شکلش در `StageFlow`.
 *
 * چرا نمونه کنارِ هر آمار: عددِ «۱۷۴ صفحه‌ی بی‌آیتم» می‌گوید چیزی خراب است
 * ولی نمی‌گوید چه چیزی. سه ردیفِ واقعی معمولاً کافی است، و بدونشان آدم برای
 * هر سوال باید سراغِ psql برود — که یعنی این صفحه کارش را نکرده.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  UNKNOWN_CITY, opsRepo,
  type Counts, type CrawlerCity, type CrawlerFunnel, type CrawlerStage,
  type FetchBucket, type FetchExample, type SampleKind,
} from '@/core/data/opsRepo';
import {
  fetchToFlow, funnelToFlow, type FetchCounts, type FunnelCounts,
} from '@/core/lib/crawlerFlow';
import { stageReport, type StageCounts } from '@/core/lib/stageReport';
import { SankeyChart } from './SankeyChart';
import { StageFlow } from './StageFlow';
import { Samples } from './Samples';
import { PairLegend, Stat, Two, TwoSeconds, fmtNum as fmt } from '@/ui/Num';

/** کدام ستونِ `Counts` — همان دو عددی که کلِ صفحه دارد. */
type Col = 'd10' | 'run';

/**
 * **شکلِ** نمودار از ستونِ ۱۰ روز ساخته می‌شود و نه از آخرین اجرا.
 *
 * ⚠ دلیلش این است که نمودار جریان را نشان می‌دهد و جریانِ یک اجرا اغلب
 * ناقص است: اجرایی که فقط `policy` دویده صفر کاندیدا دارد و نموداری
 * می‌سازد که شبیهِ «کرالر مرده» است. ده روز پنجره‌ای است که تقریباً همیشه
 * هر ده مرحله را یک بار دیده. صفحه همین را زیرِ نمودار می‌نویسد.
 *
 * ⚠ ستونِ «آخرین اجرا» حالا **هم** می‌آید، ولی فقط به‌شکلِ عددِ دومِ کنارِ
 * هر گره — نه ضخامتِ جریان. یعنی قاعده‌ی بالا سرِ جایش است و در عینِ حال
 * نمودار دیگر تنها جای صفحه نیست که یک عدد دارد.
 */
/**
 * شمارشِ درخواست‌ها برای نمودارِ دوم — همان ستونِ ۱۰ روز، به همان دلیل.
 *
 * ⚠ `null` یعنی «کشِ گزارش هنوز کلیدهای مایگریشنِ ۰۱۱۵ را ندارد» و رابط
 * باید نمودار را **نکشد**، نه اینکه صفر بگذارد. نموداری که همه‌ی
 * درخواست‌ها را «به شبکه رفت» نشان بدهد دقیقاً همان دروغی است که این
 * نمودار برای تعمیرش نوشته شد.
 */
const fetchFlowOf = (f: CrawlerFunnel, col: Col): FetchCounts | null => {
  if (!f.fetch_skipped) return null;
  return {
    fetch_requests: f.fetch_requests[col],
    fetch_ok: f.fetch_ok[col],
    fetch_unchanged: f.fetch_unchanged[col],
    fetch_errors: f.fetch_errors[col],
    fetch_blocked: f.fetch_blocked[col],
    fetch_skipped: f.fetch_skipped[col],
    fetch_blocked_robots: f.fetch_blocked_robots?.[col] ?? 0,
    fetch_blocked_tier: f.fetch_blocked_tier?.[col] ?? 0,
    fetch_blocked_disabled: f.fetch_blocked_disabled?.[col] ?? 0,
  };
};

/**
 * هر کلیدِ `Counts`ِ قیف را به یک عدد از ستونِ خواسته‌شده صاف می‌کند.
 *
 * خروجی هم‌زمان `FunnelCounts` (سنکیِ دوم) و `StageCounts` (نمودارِ
 * مرحله‌ها) است: هر دو زیرمجموعه‌ی همین کلیدهایند و دو بار پیمودنِ همان شیء
 * فقط راهی برای از هم فاصله گرفتنشان بود.
 */
const flowOf = (f: CrawlerFunnel, col: Col): FunnelCounts & StageCounts => {
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(f as unknown as Record<string, unknown>)) {
    if (value && typeof value === 'object' && 'd10' in (value as object)) {
      out[key] = (value as Counts)[col];
    }
  }
  return out as unknown as FunnelCounts & StageCounts;
};

const ago = (iso: string | null): string => {
  if (!iso) return 'هرگز';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins} دقیقه پیش`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} ساعت پیش`;
  return `${Math.round(hours / 24)} روز پیش`;
};

/** پیامِ واقعیِ خطا، نه `[object Object]` — نگاه کن به سرِ فایل. */
const errText = (e: unknown): string => {
  if (typeof e === 'string') return e;
  if (e && typeof e === 'object') {
    const o = e as { message?: unknown; details?: unknown; hint?: unknown };
    return [o.message, o.details, o.hint].filter((x) => typeof x === 'string').join(' · ')
      || JSON.stringify(e);
  }
  return String(e);
};

/**
 * ریزش‌هایی که ارزشِ نگاه کردن دارند، با نمونه‌ی متناظرشان.
 *
 * ترتیب از بالادست به پایین‌دست است و همین ترتیب حرفش را می‌زند: اگر
 * درخواست‌ها مسدود شوند، هیچ عددِ پایین‌تری معنی ندارد.
 */
const DROPS: { key: string; label: string; sample: SampleKind; why: string }[] = [
  {
    key: 'fetch_errors', label: 'درخواستِ ناموفق', sample: 'failed_fetch',
    why: 'صفحه اصلاً گرفته نشد — خطای شبکه، تایم‌اوت، یا ۴۰۰ به بالا. بالادست‌ترین ریزش: هر چه این‌جا نیاید، هیچ‌جای بعدی هم نیست.',
  },
  {
    key: 'detect_barren', label: 'صفحه‌ی بی‌آیتم', sample: 'barren_listing',
    why: 'صفحه سالم گرفته شد و تکه شد ولی صفر کاندیدا داد — یعنی الگوی تشخیص با ساختارِ سایت نمی‌خواند.',
  },
  {
    key: 'cand_claims_none', label: 'کاندیدای بی‌ادعا', sample: 'no_claims',
    why: 'تکه‌ای که کاندیدا شمرده شد ولی استخراج هیچ فیلدی از آن درنیاورد. معمولاً یعنی تکه اصلاً رویداد نبوده.',
  },
  {
    key: 'cand_no_mention', label: 'فیلدِ ناکافی', sample: 'no_mention',
    why: 'ادعا داشت ولی نه آن‌قدر که بشود گفت یک رویداد است (عنوان + تاریخ). بزرگ‌ترین ریزشِ کلِ خط.',
  },
  {
    key: 'match_uncertain', label: 'جفتِ نامطمئن', sample: 'uncertain_match',
    why: 'دو mention که matcher نتوانست بگوید یکی‌اند یا نه. ریزش نیست، ولی هر کدامش می‌تواند یک رویدادِ تکراری در اپ باشد.',
  },
  {
    key: 'events_no_date', label: 'بدونِ تاریخ', sample: 'no_date',
    why: 'بدونِ تاریخ منتشر نمی‌شود. معمولاً یعنی استخراجِ تاریخ برای آن منبع کار نکرده.',
  },
  {
    key: 'events_no_url', label: 'بدونِ آدرس', sample: 'no_url',
    why: 'بدونِ لینک هم منتشر نمی‌شود — کاربر جایی برای رفتن ندارد.',
  },
  {
    key: 'policy_review', label: 'حکمِ بازبینی', sample: 'policy_review',
    why: 'سیاستِ انتشار نه «آری» گفت نه «نه». تا وقتی این‌جاست به اپ نمی‌رود.',
  },
  {
    key: 'policy_reject', label: 'حکمِ رد', sample: 'policy_reject',
    why: 'سیاست ردش کرد — معمولاً لینکِ رقیب یا منبعِ خاموش. ستونِ دلیل در نمونه می‌گوید کدام.',
  },
];

/**
 * اسلاگ → چیزی که آدم می‌خواند. فقط سطلِ «بدونِ شهر» ترجمه می‌شود؛ بقیه
 * همان اسلاگِ دیتابیس‌اند و عمداً دست‌نخورده می‌مانند، چون همان چیزی است که
 * آدم در `sources.yaml` و در کوئری‌ها می‌بیند.
 */
const cityLabel = (c: string) => (c === UNKNOWN_CITY ? 'بدونِ شهر' : c);

/**
 * نمونه‌ی واقعیِ داخلِ کارتِ شناور — نمودارِ درخواست‌ها.
 *
 * همگام است و کوئری نمی‌زند: ردیف‌ها همراهِ خودِ قیف آمده‌اند
 * (`fetch_examples`، مایگریشنِ ۰۱۱۵). کارتی که زیرِ اشاره‌گر باز می‌شود
 * فرصتِ «…» ندارد.
 */
function fetchExample(ex: CrawlerFunnel['fetch_examples'], id: string): React.ReactNode {
  const row = ex?.[id as FetchBucket] as FetchExample | null | undefined;
  if (!row) return null;
  return (
    <div className="tipex">
      نمونه‌ی واقعی:
      <span className="mono">{row.url}</span>
      <span className="mono">
        {row.source_id}
        {row.http_status ? ` · HTTP ${row.http_status}` : ''}
        {row.detail ? ` · ${row.detail}` : ''}
      </span>
    </div>
  );
}

/**
 * همان کار برای نمودارِ خطِ لوله، ولی **تنبل**: این ردیف‌ها در قیف نیستند و
 * از `crawler_samples` می‌آیند. کوئری تازه وقتی می‌رود که کارت باز شده باشد،
 * و react-query جوابش را نگه می‌دارد؛ پس بارِ دوم فوری است.
 *
 * ⚠ فقط چهار گره نمونه‌ی متناظر دارند. بقیه هیچ‌چیز نشان نمی‌دهند و این
 * درست است — «کاندیدا» یا «خوشه» یک وضعیت است، نه یک ریزش با ردیفِ نمونه.
 */
const FUNNEL_SAMPLE: Partial<Record<string, SampleKind>> = {
  nomention: 'no_mention',
  review: 'policy_review',
  rejected: 'policy_reject',
  inapp: 'published',
};

function TipSample({ kind }: { kind: SampleKind }) {
  const rows = useQuery({
    queryKey: ['sample', kind, 1],
    queryFn: () => opsRepo.samples(kind, 1),
  });
  const row = rows.data?.[0] as Record<string, unknown> | undefined;
  if (!row) return null;
  // ستون‌ها از نوعی به نوعِ دیگر فرق می‌کنند؛ دو تای اولِ رشته‌ای معمولاً
  // منبع و آدرس‌اند و همان چیزی که آدم می‌خواهد ببیند.
  const shown = Object.entries(row)
    .filter(([, v]) => typeof v === 'string' && v.length > 0)
    .slice(0, 2);
  if (!shown.length) return null;
  return (
    <div className="tipex">
      نمونه‌ی واقعی:
      {shown.map(([k, v]) => (
        <span className="mono" key={k}>{String(v)}</span>
      ))}
    </div>
  );
}

/**
 * یک جمله‌ی مشترکِ زیرِ هر دو نمودار.
 *
 * بدونش دو عددِ کنارِ هر گره مبهم‌اند — کدام ده‌روزه است و کدام آخرین اجرا؟
 * کارت‌های بالای صفحه برچسبِ خودشان را دارند، ولی در SVG جا برای دو برچسبِ
 * دیگر نیست.
 */
function ChartLegend() {
  return (
    <p className="note legend" style={{ marginBottom: 8 }}>
      کنارِ هر گره دو عدد است: اولی <b>۱۰ روزِ اخیر</b> و دومیِ کم‌رنگ{' '}
      <b>آخرین اجرا</b>. موس را روی هر برچسب نگه دارید تا توضیحِ آن پله و یک
      ردیفِ واقعی از دیتابیس بیاید.
    </p>
  );
}

function StagesTable({ rows, filtered }: { rows: CrawlerStage[]; filtered: boolean }) {
  return (
    <div className="scrollx">
      <table className="tbl">
        <thead>
          <tr>
            <th>فاز</th><th className="num">اجرا</th><th className="num">شکست</th>
            <th className="num">میانه</th><th className="num">صدکِ ۹۵</th>
            <th>آخرین اجرا</th><th>آخرین خطا</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.stage}>
              <td>
                <b className="ltr">{s.stage}</b>
                {/* فقط وقتی فیلتر روشن است معنی دارد: این فاز به منبع بند
                    نیست، پس اعدادش مالِ همه‌ی شهرهاست نه شهرِ انتخاب‌شده.
                    بدونِ این نشان، آدم عددِ سراسری را به پای یک شهر می‌نویسد. */}
                {filtered && !s.source_scoped ? (
                  <span className="dim" style={{ fontSize: 11, marginRight: 6 }}>
                    سراسری
                  </span>
                ) : null}
              </td>
              <td className="num"><Two v={s.runs} /></td>
              <td className="num"><Two v={s.failures} bad /></td>
              <td className="num"><TwoSeconds v={s.p50_seconds} /></td>
              {/* صدکِ ۹۵ کنارِ میانه می‌آید و نه میانگین: یک اجرای طولانی در
                  میانِ پنجاه اجرای کوتاه، میانگین را بی‌معنی می‌کند ولی خودش
                  دقیقاً همان چیزی است که باید دیده شود. */}
              <td className="num">
                <TwoSeconds v={s.p95_seconds}
                  bad={s.p95_seconds?.d10 != null && s.p50_seconds?.d10 != null
                    && s.p95_seconds.d10 > s.p50_seconds.d10 * 5} />
              </td>
              <td className="dim">{ago(s.last_run)}</td>
              <td className="mono err">{s.last_error ? s.last_error.slice(0, 90) : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CrawlerTab() {
  const [city, setCity] = useState('');

  // `city` در کلیدِ **هر سه** کوئری هست. بدونش react-query جوابِ شهرِ قبلی را
  // کهنه نگه می‌دارد و عوض کردنِ شهر عددی را تکان نمی‌دهد — که دقیقاً شبیهِ
  // «فیلتر کار نمی‌کند» به نظر می‌رسد.
  const funnel = useQuery({
    queryKey: ['funnel', city],
    queryFn: () => opsRepo.crawlerFunnel(city || null),
  });
  const stages = useQuery({
    queryKey: ['stages', city],
    queryFn: () => opsRepo.crawlerStages(city || null),
  });
  const sources = useQuery({
    queryKey: ['sources', city],
    queryFn: () => opsRepo.crawlerSources(city || null),
  });
  const cities = useQuery({ queryKey: ['cities'], queryFn: () => opsRepo.crawlerCities() });

  if (funnel.isLoading) return <p className="dim">…</p>;
  if (funnel.error) {
    return (
      <div className="card">
        <h2>خطا</h2>
        <p className="err mono">{errText(funnel.error)}</p>
        <p className="note">
          اگر نامِ یک جدولِ <span className="mono">crawler.*</span> در پیامِ بالاست،
          یعنی مخزنِ event_crawler اسکیمایش را عوض کرده و مایگریشنِ متناظر در
          این مخزن هنوز نوشته نشده. آخرینش ۰۰۹۰ است.
        </p>
      </div>
    );
  }

  const f = funnel.data!;
  if (f.error) {
    return (
      <div className="card">
        <h2>در دسترس نیست</h2>
        <p className="dim">
          {f.error === 'not-precomputed'
            ? 'این گزارش هنوز ساخته نشده. گزارش‌ها را کرالر می‌سازد؛ تا اجرای'
              + ' بعدی‌اش این صفحه چیزی برای نشان دادن ندارد.'
            : 'این دیتابیس اسکیمای crawler را ندارد — این صفحه فقط روی'
              + ' دیتابیسی معنی دارد که کرالر تویش می‌نویسد.'}
        </p>
      </div>
    );
  }

  const flow = funnelToFlow(flowOf(f, 'd10'), flowOf(f, 'run'));
  const fetchD10 = fetchFlowOf(f, 'd10');
  const fetchRun = fetchFlowOf(f, 'run');
  const fetchFlow = fetchD10 ? fetchToFlow(fetchD10, fetchRun ?? undefined) : null;
  // «چند صفحه به ازای هر منبع» — تنها عددی که «درخواست» را کنارِ «اجرا»
  // قابلِ فهم می‌کند. هر دو از ستونِ ده‌روزه، مثلِ خودِ نمودار.
  const perRun = (f.fetch_runs?.d10 ?? 0) > 0
    ? Math.round(f.fetch_requests.d10 / f.fetch_runs!.d10)
    : 0;
  // «سند» در نمودار و در متن از ستونِ ده‌روزه می‌آید، مثل خودِ نمودار.
  const docs = f.docs_listing.d10 + f.docs_detail.d10;
  // «از کاندیدا تا انتشار» و نه «از mention تا انتشار»: کاندیدا اولین واحدی
  // است که واقعاً یک رویدادِ نامزد است، و بزرگ‌ترین ریزش هم بینِ همین دو تاست.
  const yieldPct = f.candidates.d10 > 0
    ? Math.round((f.publishable_mapped.d10 / f.candidates.d10) * 1000) / 10
    : 0;

  return (
    <>
      <div className="topbar" style={{ borderBottom: 'none', paddingBottom: 0 }}>
        <div className="filters">
          <label className="dim" htmlFor="city">شهر</label>
          {/* فیلدِ متنی نیست: اسلاگ را باید حدس می‌زدی و یک غلطِ تایپی
              داشبوردی سراسر صفر می‌داد که شبیهِ جوابِ درست است. */}
          <select id="city" value={city} disabled={!f.city_filter_supported}
            onChange={(e) => setCity(e.target.value)}>
            <option value="">همه</option>
            {(cities.data ?? []).map((c: CrawlerCity) => (
              <option key={c.city} value={c.city}>
                {cityLabel(c.city)} ({c.sources})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* سطلِ «بدونِ شهر» دُمِ خالی دارد و این باید همین‌جا گفته شود، وگرنه
          آدم دنبالِ باگی می‌گردد که وجود ندارد: منبعی که در رجیستری نیست
          شهرش معلوم نیست، پس سهمش در انتشار قابلِ نسبت دادن هم نیست. */}
      {city === UNKNOWN_CITY ? (
        <p className="note">
          این‌ها منابعی‌اند که در <span className="mono">crawler.sources</span> ردیف
          ندارند — شناسه‌ی <span className="mono">draft:…</span>، یعنی کرالر پیدایشان
          کرده ولی هنوز به رجیستری ترفیع نگرفته‌اند. عمداً جدا شمرده می‌شوند تا
          بی‌صدا از نمودار نیفتند. صفر بودنِ سمتِ راستِ نمودار درست است، نه
          گم‌شده: شهرشان معلوم نیست، پس سهمشان در انتشار هم نیست.
        </p>
      ) : null}

      <PairLegend run={f.run} />

      {/* ══ ۱ ــ Fetch: اصلاً به شبکه زده؟ ══ */}
      <h3 style={{ margin: '14px 0 6px' }}>گرفتنِ صفحه</h3>
      {/* ⚠ پنج کارت و نه چهار تا: تا مایگریشنِ ۰۱۱۵ کارتِ «رد شده» نبود و
          ردیف بی‌صدا کم می‌آورد — «۳۵٬۱۷۸ درخواست» بالای «۱۱٬۸۴۸ گرفته‌شده»
          و «۱۲٬۰۹۱ خطا/مسدود»، و یازده هزار تا هیچ‌جا. حالا این چهار تا
          دقیقاً جمع می‌شوند. */}
      <div className="grid n5">
        <Stat label="درخواست" v={f.fetch_requests} />
        <Stat label="گرفته‌شده"
          v={{
            total: (f.fetch_ok.total ?? 0) + (f.fetch_unchanged.total ?? 0),
            d10: f.fetch_ok.d10 + f.fetch_unchanged.d10,
            run: f.fetch_ok.run + f.fetch_unchanged.run,
          }} />
        {/* «رد شده» یعنی اصلاً به شبکه نرفت، و با «عوض نشده بود» یکی نیست:
            آن یکی رفتن و جوابِ ۳۰۴ گرفتن است. */}
        <Stat label={<>رد شده <span className="dim">(کشِ خطا)</span></>}
          tone="warn" v={f.fetch_skipped} />
        <Stat label="خطا / مسدود" tone="warn"
          v={{
            total: (f.fetch_errors.total ?? 0) + (f.fetch_blocked.total ?? 0),
            d10: f.fetch_errors.d10 + f.fetch_blocked.d10,
            run: f.fetch_errors.run + f.fetch_blocked.run,
          }} />
        <div className="card stat">
          <div className="n" style={{ fontSize: 17, paddingTop: 6 }}>
            {ago(f.last_pipeline_run)}
          </div>
          <div className="k">آخرین اجرای کاملِ خطِ لوله</div>
          {/* ⚠ اجرای کامل و «تازه‌ترین ردیفِ اجرا» یکی نیستند: بررسی و تستِ
              صفِ ادمین هم اجرا ثبت می‌کنند. اگر گفته نشود، آدم فکر می‌کند
              کرالر از دیشب دست نزده — یا بدتر، برعکس. */}
          {f.last_any_run && f.last_any_run !== f.last_pipeline_run ? (
            <div className="tot">آخرین اجرا از هر نوع: {ago(f.last_any_run)}</div>
          ) : null}
        </div>
      </div>
      {/* ⚠ `fetch_events` جدولِ تازه‌ای است و سندهای قدیمی رد پایی در آن
          ندارند. بدونِ این جمله، «۱۲۱۲ درخواست» کنارِ «۴۵۷۴ سند» شبیهِ
          تناقض به نظر می‌رسد، در حالی که دو چیزِ متفاوت را می‌شمارند. */}
      <p className="note" style={{ marginBottom: 14 }}>
        شمارشِ درخواست از <span className="mono">crawler.fetch_events</span> می‌آید
        که جدولِ تازه‌ای است؛ سندهای قدیمی‌تر ردی در آن ندارند. پس با شمارِ
        سندهای پایین جمع نمی‌خواند و نباید هم بخواند.
      </p>

      {/* ══ ۲ ــ سرنوشتِ درخواست‌ها ══ */}
      <div className="card">
        <h2>سرنوشتِ درخواست‌ها</h2>
        {/* ⚠ این نمودار عمداً از نمودارِ پایین جداست و ادامه‌اش نیست:
            واحدش «یک آدرس» است و آن یکی «یک رویدادِ نامزد». یک صفحه‌ی
            فهرست سی کاندیدا می‌دهد، پس چسباندنشان به هم عددی می‌سازد که
            هیچ چیزی را نمی‌شمارد. توضیحِ کاملش سرِ `crawlerFlow.ts`. */}
        <p className="dim" style={{ marginBottom: 6 }}>
          واحدِ این نمودار <b>یک آدرس</b> است. سوالش هم یکی است: از آدرس‌هایی
          که در صف بودند، چند تا واقعاً به شبکه رسید و بقیه کجا ماند.
        </p>
        {fetchFlow ? (
          <>
            <ChartLegend />
            {/* ستونِ آخر هشت گره دارد و چند تایشان خیلی کوچک‌اند؛ بدونِ
                فاصله‌ی دستی برچسب‌هایشان روی هم می‌افتند. */}
            <SankeyChart nodes={fetchFlow.nodes} links={fetchFlow.links}
              height={560} nodePadding={30}
              example={(id) => fetchExample(f.fetch_examples, id)} />
            {/* ⚠ این بند جوابِ سوالی است که این صفحه واقعاً ساخت:
                «۳٬۴۰۶ درخواست ولی فقط ۶۲ اجرا؟» — دو واحدِ متفاوت، و بدونِ
                این جمله آدم دنبالِ باگی می‌گردد که وجود ندارد. */}
            <p className="note">
              «اجرا» در جدولِ فازها یعنی یک بار دویدنِ فازِ{' '}
              <span className="mono">fetch</span> روی <b>یک منبع</b>، و «درخواست»
              یعنی <b>یک آدرس</b>. در ده روزِ اخیر{' '}
              {fmt(f.fetch_runs?.d10)} اجرا و {fmt(f.fetch_requests.d10)} درخواست
              {perRun > 0 ? <> — یعنی حدودِ {fmt(perRun)} صفحه به ازای هر منبع</> : null}
              : {fmt(f.fetch_listing?.d10)} صفحه‌ی فهرست که ورق خورده، و{' '}
              {fmt(f.fetch_detail?.d10)} صفحه‌ی خودِ رویدادها.
            </p>
            <p className="note">
              «کشِ لینکِ مرده» با «عوض نشده بود» یکی نیست و این تفاوت کلِ دلیلِ
              وجودِ این نمودار است: دومی یعنی رفتیم و سرور گفت چیزی عوض نشده
              (کشِ واقعی)، اولی یعنی آن آدرس سه بار پشتِ سرِ هم خطا داده و
              کرالر هفت روز سراغش نمی‌رود. روی داده‌ی امروز اولی ده برابرِ
              دومی است.
            </p>
          </>
        ) : (
          <p className="dim">
            این نمودار به کلیدهای تازه‌ی مایگریشنِ ۰۱۱۵ در گزارش نیاز دارد و کشِ
            گزارش هنوز شکلِ قدیم را دارد. گزارش‌ها را کرالر می‌سازد؛ تا اجرای
            بعدی‌اش این نمودار نمی‌آید. بقیه‌ی صفحه سرِ جایش است.
          </p>
        )}
      </div>

      {/* ══ ۳ ــ چه داریم ══ */}
      <h3 style={{ margin: '14px 0 6px' }}>چه ساخته شد</h3>
      <div className="grid n4">
        <Stat label={<>سندِ خام <span className="dim">(فهرست + جزئیات)</span></>}
          v={{
            total: (f.docs_listing.total ?? 0) + (f.docs_detail.total ?? 0),
            d10: f.docs_listing.d10 + f.docs_detail.d10,
            run: f.docs_listing.run + f.docs_detail.run,
          }} />
        <Stat label="کاندیدا" v={f.candidates} />
        <Stat label={<>خوشه‌ی زنده <span className="dim">
          ({fmt(f.clusters_multi_source.total)} چندمنبعی)</span></>}
          v={f.clusters}
          sub="خوشه نسل‌دار است: دو ستون یعنی «نسلِ زنده در آن پنجره ساخته شد»." />
        <Stat label="رخدادِ زنده روی اپ" tone="good" v={f.published_live} />
      </div>

      <div className="card">
        <h2>جریانِ خطِ لوله</h2>
        <p className="dim" style={{ marginBottom: 6 }}>
          قرمز یعنی «این‌جا کار تمام شد». برچسبِ هر جریان نامِ مرحله در
          ARCHITECTURE-VNEXT.md کرالر است. از هر {fmt(f.candidates.d10)} کاندیدا،
          {' '}{fmt(f.publishable_mapped.d10)} تا ({yieldPct}٪) به اپ رسیده.
        </p>
        {/* ⚠ **شکلِ** نمودار ده‌روزه است و عددِ دومِ کنارِ هر گره مالِ آخرین
            اجرا. اگر ضخامتِ جریان به آخرین اجرا بند می‌شد، اجرایی که فقط
            یک فاز دویده نموداری شبیهِ «کرالر مرده» می‌ساخت. */}
        <ChartLegend />
        <p className="dim" style={{ marginBottom: 6, fontSize: 12.5 }}>
          ضخامتِ جریان‌ها و اعدادِ دو بندِ بالا و پایینش از ستونِ{' '}
          <b>۱۰ روزِ اخیر</b> می‌آیند: یک اجرای تنها اغلب فقط چند مرحله را
          {' '}دویده و نمودارش شبیهِ خطِ لوله‌ی مرده در می‌آید.
        </p>
        <SankeyChart nodes={flow.nodes} links={flow.links}
          example={(id) => {
            const kind = FUNNEL_SAMPLE[id];
            return kind ? <TipSample kind={kind} /> : null;
          }} />
        {/* ⚠ نمودار از «کاندیدا» شروع می‌شود و نه از «سند»، چون واحدشان یکی
            نیست و گرهِ سند عددی نشان می‌داد که جمعِ خروجی‌اش بود نه شمارِ
            سند. مرحله‌ی تشخیص این‌جا به‌شکلِ جمله می‌آید تا جا نیفتد. */}
        <p className="note">
          پیش از این نمودار: از {fmt(docs)} سندِ خام،
          {' '}{fmt(f.detect_documents.d10)} تا تشخیص خورده و
          {' '}{fmt(f.detect_barren.d10)} تایشان صفر کاندیدا داده‌اند. نمودار از
          کاندیدا شروع می‌شود چون سند و کاندیدا هم‌واحد نیستند — یک سند چند
          کاندیدا می‌دهد، و ستونی که واحدِ ورودی و خروجی‌اش یکی نباشد عددِ
          بی‌معنی کنارِ برچسبش می‌نشاند.
        </p>
      </div>

      {/* ══ ۴ ــ مرحله‌به‌مرحله ══ */}
      <div className="card">
        <h2>مرحله‌به‌مرحله</h2>
        {/* ⚠ این نمودار سومی است و هیچ‌کدام از آن دو تا را تکرار نمی‌کند:
            آن‌ها جریانِ یک **واحد** را دنبال می‌کنند و این یکی به **مرحله**
            نگاه می‌کند. توضیحِ کاملش سرِ این فایل و سرِ `stageReport.ts`. */}
        {stages.isLoading ? <p className="dim">…</p> : null}
        {stages.error ? <p className="err mono">{errText(stages.error)}</p> : null}
        {/* ⚠ آرایه‌های «چرا» جدا پاس می‌شوند و نه از `flowOf`: آن تابع فقط
            کلیدهای `Counts`‌شکل را برمی‌دارد، و این‌ها آرایه‌اند. همه از
            مایگریشنِ ۰۱۴۴ می‌آیند و اختیاری‌اند — تا اجرای بعدیِ کرالر
            نیستند و پنل‌هایشان اصلاً کشیده نمی‌شوند. */}
        <StageFlow city={city} report={stageReport(
          flowOf(f, 'd10'), flowOf(f, 'run'), stages.data ?? [],
          {
            fetch_repeat: f.fetch_repeat,
            drafts_reasons: f.drafts_reasons,
            policy_reasons: f.policy_reasons,
            notsent_rows: f.notsent_rows,
          },
        )} />
      </div>

      {/* ══ ۵ ــ کجا ریخت ══ */}
      <div className="card">
        <h2>کجا ریخت</h2>
        <div className="grid n2">
          {DROPS.map((d) => {
            const value = (f as unknown as Record<string, Counts | undefined>)[d.key];
            return (
              <div key={d.key}>
                <Stat label={d.label} v={value} tone="warn" />
                <p className="dim" style={{ fontSize: 12.5, margin: '4px 0 6px' }}>{d.why}</p>
                {(value?.total ?? value?.d10 ?? 0) > 0 ? <Samples kind={d.sample} /> : null}
              </div>
            );
          })}
        </div>
      </div>

      {/* ══ ۶ ــ فازها ══ */}
      <div className="card">
        <h2>فازها</h2>
        {stages.isLoading ? <p className="dim">…</p> : null}
        {stages.error ? <p className="err mono">{errText(stages.error)}</p> : null}
        {stages.data ? <StagesTable rows={stages.data} filtered={city !== ''} /> : null}
      </div>

      {/* ══ ۷ ــ منابع ══ */}
      <div className="card">
        <h2>منابع</h2>
        {sources.isLoading ? <p className="dim">…</p> : null}
        {sources.error ? <p className="err mono">{errText(sources.error)}</p> : null}
        {sources.data ? (
          <div className="scrollx">
            <table className="tbl">
              <thead>
                <tr>
                  <th>منبع</th><th>شهر</th><th className="num">سند</th><th className="num">خطا</th>
                  <th className="num">کاندیدا</th><th className="num">mention</th>
                  <th className="num">خوشه</th><th className="num">منتشر</th>
                  <th>آخرین موفقیت</th><th>آخرین خطا</th>
                </tr>
              </thead>
              <tbody>
                {sources.data.map((s) => (
                  <tr key={s.source_id}>
                    <td className="mono">{s.source_id}</td>
                    {/* حتی با فیلترِ خاموش به درد می‌خورد: ستونی که پرِ
                        «بدونِ شهر» باشد خودش گزارشِ عقب بودنِ رجیستری است. */}
                    <td className={s.city === UNKNOWN_CITY ? 'dim' : ''}>
                      {cityLabel(s.city)}
                    </td>
                    <td className="num"><Two v={s.documents} /></td>
                    <td className="num"><Two v={s.documents_failed} bad /></td>
                    <td className="num"><Two v={s.candidates} /></td>
                    <td className="num"><Two v={s.mentions} /></td>
                    <td className="num"><Two v={s.events} /></td>
                    <td className="num"><Two v={s.published} /></td>
                    <td className="dim">{ago(s.last_success_at)}</td>
                    <td className="mono err">
                      {s.last_error ? s.last_error.slice(0, 60) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </>
  );
}
